import type { ComponentType, SVGProps } from "react";

/**
 * Bottom navigation mobile-only pour les dashboards (candidat, recruteur,
 * organisme).
 *
 * - Visible uniquement sur mobile (`md:hidden`) : sur tablette/desktop, ce
 *   composant disparaît et les dashboards conservent leur barre d'onglets
 *   horizontale classique.
 * - Affiche jusqu'à 5 items avec icône + label court.
 * - Position fixe en bas de l'écran avec respect de la safe-area iOS
 *   (`env(safe-area-inset-bottom)`), barre semi-transparente avec léger blur.
 * - Accent personnalisable par dashboard (blue / amber / dark).
 *
 * IMPORTANT : pour éviter que la barre cache le contenu en bas de page, le
 * dashboard doit ajouter un padding-bottom mobile au wrapper (ex. `pb-20
 * md:pb-0`). Ce composant lui-même n'ajoute aucun espace réservé au flux.
 */

export interface MobileBottomNavItem<T extends string = string> {
  id: T;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Optionnel : badge numérique (ex. nombre de favoris ou d'alertes) */
  badge?: number;
  /** Optionnel : pastille discrète (ex. coordonnées à compléter) */
  dot?: boolean;
}

export type MobileBottomNavAccent = "blue" | "amber" | "dark";

interface MobileBottomNavProps<T extends string> {
  items: ReadonlyArray<MobileBottomNavItem<T>>;
  active: T;
  onChange: (id: T) => void;
  accent?: MobileBottomNavAccent;
  ariaLabel?: string;
}

const ACCENTS: Record<
  MobileBottomNavAccent,
  { active: string; activeText: string; activeBg: string; badge: string }
> = {
  blue: {
    active: "text-blue-700",
    activeText: "text-blue-700",
    activeBg: "bg-blue-50",
    badge: "bg-blue-600 text-white",
  },
  amber: {
    active: "text-amber-700",
    activeText: "text-amber-700",
    activeBg: "bg-amber-50",
    badge: "bg-amber-600 text-white",
  },
  dark: {
    active: "text-gray-900",
    activeText: "text-gray-900",
    activeBg: "bg-gray-100",
    badge: "bg-gray-900 text-white",
  },
};

export default function MobileBottomNav<T extends string>({
  items,
  active,
  onChange,
  accent = "blue",
  ariaLabel = "Navigation",
}: MobileBottomNavProps<T>) {
  const a = ACCENTS[accent];

  return (
    <nav
      aria-label={ariaLabel}
      role="tablist"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <ul
        className="flex items-stretch justify-around"
        style={{ minHeight: 60 }}
      >
        {items.map((it) => {
          const isActive = it.id === active;
          const Icon = it.icon;
          return (
            <li key={it.id} className="flex-1 min-w-0">
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-current={isActive ? "page" : undefined}
                onClick={() => onChange(it.id)}
                className={`relative w-full h-full flex flex-col items-center justify-center gap-0.5 px-1 py-2 text-[11px] font-semibold transition-colors ${
                  isActive
                    ? `${a.activeText}`
                    : "text-gray-500 hover:text-gray-800 active:text-gray-900"
                }`}
              >
                <span
                  className={`relative inline-flex items-center justify-center w-9 h-9 rounded-xl transition-colors ${
                    isActive ? a.activeBg : "bg-transparent"
                  }`}
                >
                  <Icon
                    className={`w-[18px] h-[18px] ${isActive ? a.active : ""}`}
                  />
                  {typeof it.badge === "number" && it.badge > 0 && (
                    <span
                      className={`absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center leading-none ${a.badge}`}
                      aria-label={`${it.badge} non lu${it.badge > 1 ? "s" : ""}`}
                    >
                      {it.badge > 99 ? "99+" : it.badge}
                    </span>
                  )}
                  {it.dot && !it.badge && (
                    <span
                      className="absolute top-0.5 right-1 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white"
                      aria-label="Action requise"
                    />
                  )}
                </span>
                <span className="truncate max-w-full leading-tight">
                  {it.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
